import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { isLoaded, useFirestoreConnect } from 'react-redux-firebase';
import { getFirst } from '../../util/helpers/other';
import { getDomainSettingsPath, getSessionsPath } from '../../util/helpers/path';
import { getStoredOrDefault } from '../../util/web';
import { useTableDataParser } from '../../workers/dataWorkerApi';
import AccuracyReportTable from './AccuracyReportTable';
import DivergencyTable from './DivergencyTable';

const showInputDataStorageKey = 'sessionShowInputData';
const showBaseFieldsStorageKey = 'sessionShowBaseFields';
const mergeLabelsStorageKey = 'sessionMergeLabels';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  tableContainer: {
    marginBottom: '20px',
  },
}));

const AccuracyReport = (props) => {
  const classes = useStyles();
  const intl = useIntl();

  const { match } = props;
  const domain = match.params.domain;
  const session = match.params.session;

  const customSettingsKey = session ? `sett${domain}-${session}` : null;

  const { profile, defaultSettings, customSettings, sessionData, locale } = useSelector(
    ({ firebase: { profile }, firestore: { data }, locale: { locale } }) => {
      const sessions = data[`sess${domain}`];
      let sessionData = (isLoaded(sessions) && sessions && sessions[session]) || null;

      return {
        profile,
        sessions,
        sessionData,
        locale,
        defaultSettings: data[`sett${domain}`],
        customSettings: customSettingsKey && sessionData ? data[customSettingsKey] : null,
      };
    }
  );

  const settings = customSettings ?? defaultSettings;

  const queries = useMemo(() => {
    if (isLoaded(profile) && profile) {
      // Sessions query
      const _queries = [
        {
          collection: getSessionsPath(profile.company, domain),
          storeAs: `sess${domain}`,
          orderBy: ['startTime', 'desc'],
          limit: 100,
        },
      ];
      if (session && sessionData && profile) {
        // Custom settings query
        _queries.push({
          collection: getDomainSettingsPath(profile.company, domain),
          where: [['createdDate', '<', sessionData.startTime]],
          orderBy: ['createdDate', 'desc'],
          limit: 1,
          storeAs: customSettingsKey,
        });
      }
      return _queries;
    }
    return queries ?? [];
  }, [domain, session, profile, sessionData, customSettingsKey]);

  useFirestoreConnect(queries);

  let wasTask = (sessionData && sessionData.wasTask) || false;

  const showInputData = wasTask && getStoredOrDefault(showInputDataStorageKey, false);
  const showBaseFields = getStoredOrDefault(showBaseFieldsStorageKey, false);
  const mergeLabels = getStoredOrDefault(mergeLabelsStorageKey, false);

  const sett = getFirst(settings);
  const { rowsAndColumns } = useTableDataParser(sessionData, sett, showInputData, showBaseFields, mergeLabels);

  const barcodeFieldsToCheck = [];

  //getting barcode fields that need to be check
  //eslint-disable-next-line
  sett?.labelDesigns?.forEach((ld) =>
    ld.barcodePatterns.forEach((bp) => {
      bp.dataFields
        .filter((df) => Boolean(df.useCheckField))
        .forEach((df) => {
          barcodeFieldsToCheck.push({ field: df.checkField, checkField: df.name, isRequired: bp.isRequired });
        });
    })
  );

  //getting manual input fields that need to be check
  //eslint-disable-next-line
  sett?.manualInputFields
    ?.filter((mifs) => Boolean(mifs.useCheckField))
    .forEach((mif) => {
      barcodeFieldsToCheck.push({ field: mif.checkField, checkField: mif.field, isRequired: mif.mandatory });
    });

  //Filtering barcode fileds to check
  const filteredData = useMemo(() => {
    let filteredArray = [];
    let checkArray = [];

    //eslint-disable-next-line
    barcodeFieldsToCheck?.map((bfc) => {
      if (checkArray.length && filteredArray.length) {
        if (!checkArray.some((v) => v === bfc.field)) {
          checkArray.push(bfc.field);
          filteredArray.push(bfc);
        } else {
          if (bfc.isRequired === true) {
            filteredArray.splice(filteredArray.length - 1, 1, bfc);
          }
        }
      } else {
        checkArray.push(bfc.field);
        filteredArray.push(bfc);
      }
    });

    return filteredArray;
  }, [barcodeFieldsToCheck]);

  let divergentData = [];

  //eslint-disable-next-line
  const id = sett?.baseFields.address;

  const accuracyData = useMemo(() => {
    //Datable Columns and Rows
    const columnDataRows = [
      [
        intl.formatMessage({ id: 'report.convergent' }),
        intl.formatMessage({ id: 'report.divergent' }),
        intl.formatMessage({ id: 'report.empty' }),
        intl.formatMessage({ id: 'report.invalid' }),
        intl.formatMessage({ id: 'report.accuracy' }),
      ],
    ];
    const columnDataColumns = [];
    const positionDataRows = [];
    const positionDataColumns = [
      intl.formatMessage({ id: 'report.convergent' }),
      intl.formatMessage({ id: 'report.divergent' }),
      intl.formatMessage({ id: 'report.invalid' }),
      intl.formatMessage({ id: 'report.total' }),
      intl.formatMessage({ id: 'report.accuracy' }),
    ];

    //Per Columns Related
    if (filteredData.length) {
      columnDataColumns.push('');

      filteredData.forEach((fields) => {
        let convergentColumns = 0;
        let divergentColumns = 0;
        let invalidColumns = 0;
        let emptyColumns = 0;

        columnDataColumns.push(fields.field);
        //eslint-disable-next-line
        rowsAndColumns?.rows.map((row) => {
          switch (row[fields.checkField]) {
            case row[fields.field]:
              convergentColumns += 1;
              break;
            case sett.resultFields.empty:
            case sett.resultFields.null:
              convergentColumns += 1;
              emptyColumns += 1;
              break;
            case sett.resultFields.invalid:
              invalidColumns += 1;
              break;
            default:
              divergentColumns += 1;
              divergentData.push(row);
          }
        });

        columnDataRows.push([
          convergentColumns,
          divergentColumns,
          emptyColumns,
          invalidColumns,
          //eslint-disable-next-line
          `${((convergentColumns * 100) / rowsAndColumns?.rows.length).toFixed(2)}%`,
        ]);
      });

      //Per Position Related
      const fieldState = {
        INVALID: 'inválido',
        CONVERGENT: 'convergente',
        DIVERGENT: 'divergente',
      };

      const positionsCount = {
        convergentPositions: 0,
        divergentPositions: 0,
        invalidPositions: 0,
        totalPositions: 0,
        positionsAccuracy: 0,
      };

      //eslint-disable-next-line
      rowsAndColumns?.rows.map((row) => {
        positionsCount.totalPositions += 1;

        const rowData = filteredData.map((fields) => {
          if (
            row[fields.checkField] === row[fields.field] ||
            row[fields.checkField] === sett.resultFields.empty ||
            row[fields.checkField] === sett.resultFields.null
          ) {
            return fieldState.CONVERGENT;
          } else if (row[fields.checkField] === sett.resultFields.invalid) {
            return fieldState.INVALID;
          } else {
            return fieldState.DIVERGENT;
          }
        });

        if (rowData[0] === fieldState.DIVERGENT || rowData[1] === fieldState.DIVERGENT) {
          positionsCount.divergentPositions += 1;
        } else if (rowData[0] === fieldState.INVALID || rowData[1] === fieldState.INVALID) {
          positionsCount.invalidPositions += 1;
        } else {
          positionsCount.convergentPositions += 1;
        }
      });

      positionsCount.positionsAccuracy = `${(
        (positionsCount.convergentPositions * 100) /
        positionsCount.totalPositions
      ).toFixed(2)}%`;

      Object.keys(positionsCount).forEach((key) => {
        positionDataRows.push(positionsCount[key]);
      });
    }

    return { columnDataRows, columnDataColumns, positionDataRows, positionDataColumns };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsAndColumns, filteredData, intl, locale, sett]);

  return (
    <Box className={classes.container}>
      <Box>
        <Box className={classes.tableContainer}>
          <AccuracyReportTable
            title={intl.formatMessage({ id: 'report.accuracyReport' })}
            subtitle={intl.formatMessage({ id: 'report.positionStatistic' })}
            data={accuracyData}
            type={1}
          />
        </Box>
        <Box className={classes.tableContainer}>
          <AccuracyReportTable
            subtitle={intl.formatMessage({ id: 'report.columnStatistic' })}
            data={accuracyData}
            type={0}
          />
        </Box>
        {divergentData.length ? (
          <Box>
            <DivergencyTable
              title={intl.formatMessage({ id: 'report.divergentPositions' })}
              data={{ divergentRows: divergentData, checkFields: filteredData, idField: id }}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default AccuracyReport;
